import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessage } from "@fortawesome/pro-regular-svg-icons/faMessage";
import { faPen } from "@fortawesome/pro-regular-svg-icons/faPen";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faFile } from "@fortawesome/pro-regular-svg-icons/faFile";
import { faCamera } from "@fortawesome/pro-regular-svg-icons/faCamera";
import { faRepeat } from "@fortawesome/pro-regular-svg-icons/faRepeat";
import {
  bindSubmittedAnswers,
  type SignoffAttachment,
  type SubmittedAnswer,
  type SubmittedAnswerGroup,
} from "@eaco/forms-core";

import {
  useFormResultUpload,
  useResultFile,
} from "../elements/useFormResultUpload";
import { useImageLightbox } from "../elements/ImageLightbox";
import { cn } from "../lib/utils";

/**
 * The sign-off review's compact "Submitted response" list (shared by the
 * authenticated hub + the public microsite). Renders each ANSWERED element as a
 * row — status-colour bar + dot, label, value (text / image thumbnails / file
 * chip) — with a per-answer reviewer-comment interface: an "Add comment" button
 * that opens a composer, and existing comments shown as cards.
 *
 * Comments are controlled: `comments` holds the draft text keyed by element id;
 * `onCommentChange(id, text)` reports edits. In `readOnly` mode the composer is
 * hidden and `savedComments` render as read-only cards.
 *
 * Photos work the same way. A reviewer can attach evidence against a single
 * answer as well as commenting on it, which is what the Lifeblood cleaning
 * records need; `attachments` / `onAttachmentsChange` carry the drafts and
 * `savedAttachments` render beneath the saved comment. Uploading goes through
 * the host-injected transport, so every host that already renders this list
 * gets the capture without wiring anything of its own.
 *
 * The list is flat, but the form was not: answers carry the page, heading,
 * section and repeat instance they were given under, and a header is drawn
 * wherever one of those changes. A form with no pages or sections draws
 * none, so a short single-page form stays as plain as it reads.
 */
export interface SubmittedResponseReviewProps {
  content: { elements?: unknown[] } | null | undefined;
  responses: Record<string, unknown> | null | undefined;
  /** Draft comments keyed by element id (editable mode). */
  comments?: Record<string, string>;
  onCommentChange?: (elementId: string, text: string) => void;
  /** Draft photos keyed by element id (editable mode). */
  attachments?: Record<string, SignoffAttachment[]>;
  onAttachmentsChange?: (elementId: string, next: SignoffAttachment[]) => void;
  /** Read-only: no composer; show `savedComments` as cards. */
  readOnly?: boolean;
  savedComments?: Record<string, string> | null;
  savedAttachments?: Record<string, SignoffAttachment[]> | null;
  /** Initials + name for the comment-card avatar (the reviewer). */
  commenterInitials?: string;
  commenterName?: string;
  /**
   * What the commenter is to this response — "reviewer" on a saved note,
   * so a reader knows whose voice it is. Omitted while composing, where
   * the name is the author's own.
   */
  commenterRole?: string;
  heading?: string;
  className?: string;
}

const OK_COLOUR = "#1a8a5c";
const FLAG_COLOUR = "#c0303c";

const SubmittedResponseReview: React.FC<SubmittedResponseReviewProps> = ({
  content,
  responses,
  comments,
  onCommentChange,
  attachments,
  onAttachmentsChange,
  readOnly = false,
  savedComments,
  savedAttachments,
  commenterInitials = "You",
  commenterName = "You",
  commenterRole,
  heading = "Submitted response",
  className,
}) => {
  const answers = React.useMemo(
    () => bindSubmittedAnswers(content as never, responses),
    [content, responses]
  );

  if (answers.length === 0) {
    return (
      <div className={cn("text-[13px] italic text-[#8a93a3]", className)}>
        No answers to display.
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="mb-[13px] text-[12px] font-extrabold uppercase tracking-[0.07em] text-[#8a93a3]">
        {heading}
      </div>
      <div className="flex flex-col gap-[8px]">
        {answers.map((a, i) => (
          // A repeating section gives the same element several answers, so
          // the position is part of the key — the element id alone is not
          // unique down this list.
          <React.Fragment key={`${a.elementId}:${i}`}>
            <AnswerGroupHeaders
              group={a.group}
              previous={i > 0 ? answers[i - 1].group : null}
              first={i === 0}
            />
            <AnswerRow
              answer={a}
              comment={comments?.[a.elementId] ?? ""}
              savedComment={savedComments?.[a.elementId] ?? ""}
              photos={attachments?.[a.elementId] ?? EMPTY_PHOTOS}
              savedPhotos={savedAttachments?.[a.elementId] ?? EMPTY_PHOTOS}
              readOnly={readOnly}
              onCommentChange={onCommentChange}
              onAttachmentsChange={onAttachmentsChange}
              commenterInitials={commenterInitials}
              commenterName={commenterName}
              commenterRole={commenterRole}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/** Stable empty list, so a row without photos does not get a new array each render. */
const EMPTY_PHOTOS: SignoffAttachment[] = [];

/**
 * The page / heading / section headers above an answer, drawn only where
 * they change.
 *
 * A page change re-opens everything below it: a section called "General
 * areas" on page 2 is not the one on page 1, and silently carrying the
 * header over would say it was. Within a repeating section each instance
 * re-shows the section line, because the count is the only thing telling
 * the reader they have moved on to the next bathroom.
 */
const AnswerGroupHeaders: React.FC<{
  group: SubmittedAnswerGroup;
  previous: SubmittedAnswerGroup | null;
  first: boolean;
}> = ({ group, previous, first }) => {
  const pageChanged = group.page !== (previous?.page ?? null);
  const headingChanged = pageChanged || group.heading !== (previous?.heading ?? null);
  const sectionChanged =
    headingChanged ||
    group.section !== (previous?.section ?? null) ||
    group.repeatIndex !== (previous?.repeatIndex ?? null);

  const showPage = pageChanged && !!group.page;
  const showHeading = headingChanged && !!group.heading;
  const showSection =
    sectionChanged && (!!group.section || group.repeatIndex !== null);

  if (!showPage && !showHeading && !showSection) return null;

  return (
    <>
      {showPage && (
        <div
          className="flex items-center gap-[10px]"
          style={first ? undefined : { marginTop: 14 }}
        >
          <span className="whitespace-nowrap text-[11px] font-extrabold uppercase tracking-[0.06em] text-[#2f6fdb]">
            {group.page}
          </span>
          <span className="h-[1px] flex-1 bg-[#e3e7ec]" />
        </div>
      )}
      {showHeading && (
        <div className="mt-[2px] text-[15px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
          {group.heading}
        </div>
      )}
      {showSection && (
        <div className="mt-[2px] flex items-center gap-[8px]">
          {group.section && (
            <span className="text-[12.5px] font-bold text-[#6a7383]">
              {group.section}
            </span>
          )}
          {group.repeatIndex !== null && (
            <span className="inline-flex items-center gap-[5px] rounded-[6px] bg-[#f0eafb] px-[7px] py-[2px] text-[11px] font-bold text-[#5a33a8]">
              <FontAwesomeIcon icon={faRepeat} style={{ fontSize: 10 }} />
              {group.repeatCount === null
                ? group.repeatIndex
                : `${group.repeatIndex} of ${group.repeatCount}`}
            </span>
          )}
        </div>
      )}
    </>
  );
};

const AnswerRow: React.FC<{
  answer: SubmittedAnswer;
  comment: string;
  savedComment: string;
  photos: SignoffAttachment[];
  savedPhotos: SignoffAttachment[];
  readOnly: boolean;
  onCommentChange?: (elementId: string, text: string) => void;
  onAttachmentsChange?: (elementId: string, next: SignoffAttachment[]) => void;
  commenterInitials: string;
  commenterName: string;
  commenterRole?: string;
}> = ({
  answer,
  comment,
  savedComment,
  photos,
  savedPhotos,
  readOnly,
  onCommentChange,
  onAttachmentsChange,
  commenterInitials,
  commenterName,
  commenterRole,
}) => {
  const barColour = answer.flagged ? FLAG_COLOUR : OK_COLOUR;
  const [editing, setEditing] = useState(false);

  const hasDraft = comment.trim().length > 0;
  const hasSaved = savedComment.trim().length > 0;

  return (
    <div
      data-element-id={answer.elementId}
      className="flex items-stretch overflow-hidden rounded-[10px] border border-[#eef1f4] bg-[#fbfcfd]"
    >
      <span
        className="w-[4px] flex-none"
        style={{ backgroundColor: barColour }}
      />
      <div className="flex min-w-0 flex-1 items-start gap-[16px] px-[15px] py-[12px]">
        <span className="w-[170px] flex-none pt-[1px] text-[12.5px] font-semibold text-[#8a93a3]">
          {answer.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[8px]">
            <span
              className="h-[8px] w-[8px] flex-none rounded-full"
              style={{ backgroundColor: barColour }}
            />
            <AnswerValue answer={answer} />
          </div>

          {/* Existing / draft comment card */}
          {readOnly ? (
            (hasSaved || savedPhotos.length > 0) && (
              <CommentCard
                initials={commenterInitials}
                who={commenterName}
                text={savedComment}
                photos={savedPhotos}
                role={commenterRole}
              />
            )
          ) : editing ? (
            <CommentComposer
              value={comment}
              onChange={v => onCommentChange?.(answer.elementId, v)}
              onClose={() => setEditing(false)}
            />
          ) : (
            hasDraft && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="mt-[8px] block w-full text-left"
              >
                <CommentCard
                  initials={commenterInitials}
                  who={commenterName}
                  text={comment}
                  editable
                />
              </button>
            )
          )}

          {/* Capturing photos against this answer. Saved ones are not shown
              here — they render inside the reviewer's note above. */}
          {!readOnly && (
            <SignoffPhotos
              photos={photos}
              readOnly={false}
              onChange={
                onAttachmentsChange
                  ? next => onAttachmentsChange(answer.elementId, next)
                  : undefined
              }
            />
          )}

          {/* Add-comment affordance */}
          {!readOnly && !editing && !hasDraft && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="mt-[7px] inline-flex items-center gap-[5px] text-[11.5px] font-bold text-[#8a93a3] transition-colors hover:text-[#2f6fdb]"
            >
              <FontAwesomeIcon icon={faMessage} style={{ fontSize: 12 }} />
              Add comment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * The reviewer's photos: thumbnails of what is attached,
 * and — unless read-only — a picker to add more and a cross to drop one.
 *
 * Uploading is the same path the form's own image questions use, so the file
 * lands against this result and the host's transport decides how. A failed
 * upload shows inline rather than silently dropping the file.
 */
export const SignoffPhotos: React.FC<{
  photos: SignoffAttachment[];
  readOnly: boolean;
  onChange?: (next: SignoffAttachment[]) => void;
  addLabel?: string;
}> = ({ photos, readOnly, onChange, addLabel = "Add photo" }) => {
  if (readOnly && photos.length === 0) return null;

  return (
    <div className="mt-[8px]">
      {photos.length > 0 && (
        <div className="flex flex-wrap items-start gap-[8px]">
          {photos.map(photo => (
            <div key={photo.fileId} className="relative">
              <AnswerImageThumb fileId={photo.fileId} name={photo.name} />
              {!readOnly && (
                <button
                  type="button"
                  aria-label={`Remove ${photo.name || "photo"}`}
                  onClick={() =>
                    onChange?.(photos.filter(p => p.fileId !== photo.fileId))
                  }
                  className="absolute -right-[6px] -top-[6px] flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#e6e9ee] bg-white text-[#8a93a3] shadow-sm transition-colors hover:text-[#c0303c]"
                >
                  <FontAwesomeIcon icon={faXmark} style={{ fontSize: 10 }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && (
        <SignoffPhotoPicker
          photos={photos}
          onChange={onChange}
          addLabel={addLabel}
        />
      )}
    </div>
  );
};

/**
 * The add-a-photo half, kept separate so displaying photos read-only costs
 * nothing: the upload hook needs a FormFileTransport in the tree, and a
 * surface that only shows what is already attached should not have to
 * provide one.
 */
const SignoffPhotoPicker: React.FC<{
  photos: SignoffAttachment[];
  onChange?: (next: SignoffAttachment[]) => void;
  addLabel: string;
}> = ({ photos, onChange, addLabel }) => {
  const { pending, pickFiles, clearPending } = useFormResultUpload();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const onPick = React.useCallback(
    async (picked: File[]) => {
      if (picked.length === 0) return;
      const refs = await pickFiles(picked);
      if (refs.length === 0) return;
      onChange?.([...photos, ...refs]);
    },
    [pickFiles, onChange, photos]
  );

  const failed = pending.filter(p => p.state === "error");

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={ev => {
          const picked = Array.from(ev.target.files ?? []);
          // Reset first: picking the same file twice in a row fires no
          // change event otherwise.
          ev.target.value = "";
          void onPick(picked);
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="mt-[7px] inline-flex items-center gap-[5px] text-[11.5px] font-bold text-[#8a93a3] transition-colors hover:text-[#2f6fdb]"
      >
        <FontAwesomeIcon icon={faCamera} style={{ fontSize: 12 }} />
        {photos.length > 0 ? "Add another photo" : addLabel}
      </button>

      {failed.length > 0 && (
        <div className="mt-[6px] text-[11.5px] text-[#c0303c]">
          {failed.map(f => (
            <div key={f.uploadId}>
              {f.file.name}: {f.errorMessage || "Upload failed"}{" "}
              <button
                type="button"
                onClick={() => clearPending(f.uploadId)}
                className="font-bold underline"
              >
                Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/** Render a single answer's value — thumbnails for images, chips for files. */
const AnswerValue: React.FC<{ answer: SubmittedAnswer }> = ({ answer }) => {
  if (answer.hasFiles && answer.files) {
    if (answer.filesAreImages) {
      return (
        <div className="flex flex-wrap gap-[8px]">
          {answer.files.map(f => (
            <AnswerImageThumb key={f.fileId} fileId={f.fileId} name={f.name} />
          ))}
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-[6px]">
        {answer.files.map(f => (
          <AnswerFileChip key={f.fileId} fileId={f.fileId} name={f.name} />
        ))}
      </div>
    );
  }
  return (
    <span className="text-[13.5px] font-bold text-[#1f2a3d]">
      {answer.value}
    </span>
  );
};

const AnswerImageThumb: React.FC<{ fileId: number; name: string | null }> = ({
  fileId,
  name,
}) => {
  const { file } = useResultFile(fileId);
  const { openImage, lightbox } = useImageLightbox();
  if (!file?.url) {
    return (
      <span className="text-[13px] text-[#8a93a3]">{name || "Image"}</span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() =>
          openImage({
            fileId,
            name,
            previewUrl: file.thumbnailUrl ?? file.url,
            fullUrl: file.url,
          })
        }
        className="block cursor-zoom-in"
        aria-label={`View ${name || "image"}`}
      >
        <img
          src={file.thumbnailUrl ?? file.url ?? undefined}
          alt={name || "Image"}
          className="max-h-[96px] rounded-[8px] border border-[#eef1f4] bg-white object-contain"
        />
      </button>
      {lightbox}
    </>
  );
};

const AnswerFileChip: React.FC<{ fileId: number; name: string | null }> = ({
  fileId,
  name,
}) => {
  const { file } = useResultFile(fileId);
  return (
    <a
      href={file?.url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-[6px] rounded-[7px] border border-[#e6e9ee] bg-white px-[9px] py-[5px] text-[12.5px] font-semibold text-[#2f6fdb]"
    >
      <FontAwesomeIcon icon={faFile} style={{ fontSize: 12 }} />
      {name || "File"}
    </a>
  );
};

const CommentCard: React.FC<{
  initials: string;
  who: string;
  text: string;
  editable?: boolean;
  /**
   * Photos the reviewer attached. They belong to the note, so they sit
   * inside the card with it rather than loose beneath the answer.
   */
  photos?: SignoffAttachment[];
  /** Rendered after the name, e.g. "· reviewer". */
  role?: string;
}> = ({ initials, who, text, editable, photos = [], role }) => (
  <div className="mt-[8px] flex gap-[8px] rounded-[8px] border border-[#dbe7fb] bg-[#eef4fd] px-[10px] py-[8px]">
    <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-[#2f6fdb] text-[8.5px] font-extrabold text-white">
      {initials.slice(0, 2).toUpperCase()}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-[6px] text-[11px] text-[#8a93a3]">
        <b className="text-[#4a5262]">{who}</b>
        {role && <span>· {role}</span>}
        {editable && (
          <span className="inline-flex items-center gap-[3px] text-[#2f6fdb]">
            <FontAwesomeIcon icon={faPen} style={{ fontSize: 9 }} />
            Edit
          </span>
        )}
      </div>
      {text && (
        <div className="mt-[1px] whitespace-pre-wrap text-[12.5px] leading-[1.45] text-[#1f2a3d]">
          {text}
        </div>
      )}
      <SignoffPhotos photos={photos} readOnly />
    </div>
  </div>
);

const CommentComposer: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
}> = ({ value, onChange, onClose }) => (
  <div className="mt-[8px] rounded-[8px] border border-[#bcd3f5] bg-[#f5f9ff] p-[8px]">
    <div className="mb-[5px] flex items-center justify-between">
      <span className="inline-flex items-center gap-[5px] text-[11px] font-bold text-[#2f6fdb]">
        <FontAwesomeIcon icon={faMessage} style={{ fontSize: 11 }} />
        Comment on this answer
      </span>
      <button
        type="button"
        aria-label="Close comment"
        onClick={onClose}
        className="text-[#8a93a3] transition-colors hover:text-[#4a5262]"
      >
        <FontAwesomeIcon icon={faXmark} style={{ fontSize: 12 }} />
      </button>
    </div>
    <textarea
      autoFocus
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={() => {
        if (!value.trim()) onClose();
      }}
      placeholder="Add a comment on this answer…"
      rows={2}
      className="w-full resize-y rounded-[6px] border border-[#cfe0f7] bg-white px-[9px] py-[7px] text-[12.5px] text-[#1f2a3d] outline-none focus:border-[#2f6fdb]"
    />
  </div>
);

export default SubmittedResponseReview;
